import { useGetLandingImageUploadUrlMutation } from '~/shared/api'
import { getBlobFromUrl, isBlobUrl, uploadFileToPresignedUrl } from '~/shared/lib'

export function uploadProjectImage() {
  const { mutate: uploadImageUrl } = useGetLandingImageUploadUrlMutation()

  async function uploadFromBlobUrl(blobUrl: string): Promise<string | null> {
    if (!isBlobUrl(blobUrl)) {
      return blobUrl
    }
    const blob = await getBlobFromUrl(blobUrl)
    const [category = 'image', extension = category === 'video' ? 'mp4' : 'png'] = blob.type.split('/')
    const file = new File(
      [blob],
      `${category}-${Date.now()}.${extension}`,
      { type: blob.type || `${category}/${extension}` },
    )
    return uploadFile(file)
  }

  async function uploadFile(file: File): Promise<string | null> {
    if (!file)
      return null

    const result = await uploadImageUrl({
      input: {
        fileName: file.name,
        contentType: file.type,
        folder: 'templates',
      },
    })

    if (result?.data?.getLandingImageUploadUrl) {
      const { uploadUrl, publicUrl } = result.data.getLandingImageUploadUrl

      await uploadFileToPresignedUrl(file, uploadUrl)
      return publicUrl
    }

    return null
  }

  return {
    uploadFile,
    uploadFromBlobUrl,
  }
}

export function isPendingProjectMediaUpload(file: string | null | undefined): boolean {
  if (!file) {
    return false
  }

  return file.startsWith('data:') || file.startsWith('blob:')
}

export async function uploadProjectLogo(file: string, projectId: string) {
  const { getFileExtensionFromDataURL, getFileFromDataURL, uploadFileToPresignedUrl } = await import('~/shared/lib')
  const extension = getFileExtensionFromDataURL(file) ?? 'png'

  const { useProjectRequestPresignedPutUrlLazyQuery, ProjectMediaType } = await import('~/shared/api')

  const data = await useProjectRequestPresignedPutUrlLazyQuery(
    {
      input: {
        mediaType: ProjectMediaType.Logo,
        projectId,
        filename: `logo.${extension}`,
      },
    },
    { fetchPolicy: 'no-cache' },
  ).load()

  if (!data?.projectRequestPresignedPutUrl?.url) {
    return
  }

  await uploadFileToPresignedUrl(
    getFileFromDataURL(file, `logo-${projectId}.${extension}`),
    data.projectRequestPresignedPutUrl.url,
  )
}

export async function uploadProjectLogoIfPending(
  file: string | null | undefined,
  projectId: string,
) {
  if (!isPendingProjectMediaUpload(file)) {
    return
  }

  await uploadProjectLogo(file!, projectId)
}

export async function uploadProjectFile(file: string, projectId: string) {
  const { getFileExtensionFromDataURL, getFileFromDataURL, uploadFileToPresignedUrl } = await import('~/shared/lib')
  const extension = getFileExtensionFromDataURL(file)

  if (!extension) {
    return
  }

  const { useProjectRequestPresignedPutUrlLazyQuery } = await import('~/shared/api')
  const { ProjectMediaType } = await import('~/shared/api')

  const data = await useProjectRequestPresignedPutUrlLazyQuery(
    { input: { mediaType: ProjectMediaType.Image, projectId } },
    { fetchPolicy: 'no-cache' },
  ).load()

  if (!data) {
    return
  }

  await uploadFileToPresignedUrl(
    getFileFromDataURL(
      file,
      `project-${projectId}`,
    ),
    data.projectRequestPresignedPutUrl.url,
  )
}
